import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreateIdentifier } from 'src/features/membership/api/identifier/useCreateIdentifier';
import { LIST_IDENTIFIERS_RESOURCE } from 'src/features/membership/api/identifier/useListIdentifiers';
import IdentifierFormModal from 'src/features/membership/components/forms/identifier/IdentifierFormModal';
import { MembershipIdentifier } from 'src/features/membership/types';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier?: MembershipIdentifier;
    children?: React.ReactNode;
}

function CreateIdentifierFormModal<TData>({ className, semantics }: IdentifierFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { membershipId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateIdentifier({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_IDENTIFIERS_RESOURCE] });
            toast({
                title: formatMessage(identifiersMessages.createIdentifierSuccess),
                description: formatMessage(identifiersMessages.createIdentifierSuccessDescription, {
                    entry: success.identifier.value,
                }),
            });
        },
    });

    return (
        <IdentifierFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ ...values, membershipId, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(identifiersMessages.addIdentifierSubtitle)}
        />
    );
}

export default CreateIdentifierFormModal;
